import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Brand } from "./Brand";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { InkCap, InkGrowth } from "./InkIcons";
import { useLang } from "../lib/lang";
import { WA_DEFAULT } from "../lib/copy";
import { trackWhatsApp } from "../lib/analytics";

const ease = [0.22, 1, 0.36, 1];

const BookStack = ({ labels }) => {
  // Rising steps: Upskill sits lowest and furthest left, Achieve is the top step
  const styles = ["bg-white text-ink -rotate-2", "bg-sky text-ink rotate-1 ml-6", "bg-sun text-night -rotate-1 ml-12"];
  return (
    <div className="flex flex-col-reverse gap-1.5" data-testid="hero-book-stack">
      {labels.map((l, i) => (
        <motion.div key={l} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 + i * 0.15, duration: 0.6, ease }}
          className={`relative flex h-11 w-40 items-center rounded-md pl-4 font-display text-sm font-extrabold uppercase tracking-wider shadow-[0_12px_24px_-10px_rgba(0,0,0,0.45)] sm:w-48 ${styles[i]}`}>
          <span className="absolute inset-y-1.5 left-1.5 w-1 rounded bg-current opacity-30" />
          {l}
        </motion.div>
      ))}
    </div>
  );
};

const HeroVisual = ({ t, y1, y2 }) => (
  <div className="relative mx-auto aspect-square w-full max-w-[340px] sm:max-w-[460px] lg:max-w-[520px]">
    <motion.div initial={{ y: 140, scale: 0.85, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} transition={{ duration: 1.3, ease }}
      className="absolute inset-0 rounded-full bg-sun" aria-hidden="true" />
    <div className="sun-orbit absolute -inset-5 rounded-full border-2 border-dashed border-white/20" aria-hidden="true" />
    <motion.div style={{ y: y1 }} className="absolute inset-[7%] overflow-hidden rounded-full ring-8 ring-ink/10">
      <motion.img initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease }}
        src="/img/hero-student.webp" alt={t.hero.alt} width="1000" height="1000" fetchPriority="high"
        className="h-full w-full object-cover object-[50%_30%]" />
    </motion.div>
    <motion.div style={{ y: y2 }} className="absolute -bottom-4 -left-2 sm:-left-10">
      <BookStack labels={t.hero.books} />
    </motion.div>
    <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.4, duration: 0.5, ease }}
      className="absolute right-0 top-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-ink shadow-xl sm:-right-4">
      <InkCap className="h-9 w-9" />
    </motion.div>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.5, ease }}
      className="absolute -right-1 bottom-16 flex h-14 w-14 items-center justify-center rounded-full bg-night text-white shadow-xl sm:-right-6">
      <InkGrowth className="h-8 w-8" />
    </motion.div>
  </div>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ink pb-28 pt-14 text-white sm:pt-20 lg:pb-36" data-testid="hero-section">
      <div className="dot-grid pointer-events-none absolute inset-0 opacity-40" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:gap-10">
        <div>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-sun backdrop-blur">
            <span className="h-2 w-2 animate-pulse rounded-full bg-sun" />{t.hero.eyebrow}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-7 text-5xl text-white sm:text-6xl" data-testid="hero-brand-name">
            <Brand className="brand-stack" />
          </motion.p>
          <h1 key={lang} className={`mt-7 font-display font-extrabold tracking-tight ${lang === "hi" ? "text-[2.3rem] leading-[1.2] sm:text-5xl lg:text-[3.6rem]" : "text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-[4.6rem]"}`} data-testid="hero-headline">
            {t.hero.lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span className={`block ${i === t.hero.lines.length - 1 ? "text-sun" : ""}`}
                  initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">{t.hero.sub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("hero")} data-testid="hero-get-counselling-button"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-7 py-4 font-display text-base font-extrabold text-night transition-[background-color,box-shadow] hover:bg-[#ffb63d] hover:shadow-[0_16px_40px_-14px_rgba(245,165,36,1)]">
              <MessageCircle className="h-5 w-5" aria-hidden="true" />{t.hero.primary}
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
            </a>
            <Link to="/programmes" data-testid="hero-explore-programs-button"
              className="inline-flex items-center justify-center rounded-full border-2 border-white/25 px-7 py-4 font-display text-base font-bold text-white transition-colors hover:border-white hover:bg-white/5">
              {t.hero.secondary}
            </Link>
          </motion.div>
        </div>
        <div>
          <HeroVisual t={t} y1={y1} y2={y2} />
          <p className="mt-8 text-center text-xs italic text-white/75" data-testid="hero-img-note">{t.imgNote}</p>
        </div>
      </div>
    </section>
  );
};
